import React, { useEffect, useMemo } from 'react';
import { Flex, Gap } from 'lib/components/styles/layout';
import { useFormikContext } from 'formik';
import { ILandingPageBuilderForm } from '../../types';
import { Button, useCovideoTheme } from 'react-covideo-common';
import { FaAlignCenter, FaAlignLeft, FaAlignRight } from 'react-icons/fa';
import {
  FontCases,
  FontSizes,
  LP_BUILDER_FORM_MODE,
  LpBuilderFormMode,
  TEXT_ALIGNMENT,
  TEXT_ORIENTATION,
} from '../../constants';
import { FormikRadioField } from 'lib/components/formik/FormikRadioField';
import { FormikImageUpload } from 'lib/components/formik/FormikImageUpload/FormikImageUpload';
import { FormSection } from './shared/FormSection';
import { FormContainer } from './shared/FormContainer';
import { FormikSwitchField } from 'lib/components/formik/FormikSwitchField';
import { FormikCheckboxField } from 'lib/components/formik/FormikCheckbox';
import { FormikDropdownField } from 'lib/components/formik/FormikDropdown';
import { FormikNumberField } from 'lib/components/formik/FormikNumberField';
import { FontFamilyOption } from 'app/pages/design/landingPageBuilder/components';
import { getFilteredFontWeights } from 'lib/utils/getFilteredFontWeights';
import { FormikColorPicker } from 'lib/components/formik/FormikColorPicker';
import { ILandingPageBuilderBulkEditFormData } from '../../helpers/getInitialLandingPageValuesForBulkEdit';

interface IHeaderFormProps {
  allFontFamilyOptions: FontFamilyOption[];
  mode: LpBuilderFormMode;
}

export const HeaderForm = ({
  allFontFamilyOptions,
  mode,
}: IHeaderFormProps) => {
  const themes = useCovideoTheme();
  const { values, setFieldValue } = useFormikContext<ILandingPageBuilderForm>();

  const commonLabelStyle = {
    labelStyles: {
      fontSize: '16px',
      marginLeft: '7px',
      fontWeight: 400,
      color: themes.colors.neutral[100],
    },
  };

  const commonSelectedStyle = {
    container: { border: `1px solid ${themes.colors.secondary[40]}` },
  };

  const commonFormikColorPickerStyle = {
    labelStyles: {
      fontSize: '12px',
      color: themes.colors.neutral[80],
      fontWeight: 600,
      marginBottom: '8px',
    },
  };

  const { filteredFontWeights, fallbackWeight } = useMemo(() => {
    return getFilteredFontWeights({
      fontFamilyValue: values.headerFontFamily,
      fontWeightValue: values.headerFontWeight,
      allFontFamilyOptions,
    });
  }, [values.headerFontFamily]);

  useEffect(() => {
    // if we are in bulk edit mode and there are no font weights available — it means the font family has been deselected
    // set the font weight to null
    if (!isBulkEditMode) return;
    if (!filteredFontWeights.length) {
      setFieldValue('headerFontWeight', null);
    }
  }, [filteredFontWeights]);

  useEffect(() => {
    if (fallbackWeight) {
      setFieldValue('headerFontWeight', fallbackWeight);
    }
  }, [fallbackWeight]);

  const isBulkEditMode = mode === LP_BUILDER_FORM_MODE.BULK_EDIT;

  // font size options should have "None" option in bulk edit mode
  const fontSizeOptions = isBulkEditMode
    ? [{ label: 'None', value: null }, ...FontSizes]
    : FontSizes;

  // font case options should have "None" option in bulk edit mode
  const fontCaseOptions = isBulkEditMode
    ? [{ label: 'None', value: null }, ...FontCases]
    : FontCases;

  const onFormikNumberInputBlur = (field: keyof ILandingPageBuilderForm) => {
    // do not use custom blur if not in bulk edit mode
    if (!isBulkEditMode) return;
    if (values[field] === '') {
      setFieldValue(field, null);
    }
  };

  const handleHeaderAlignmentClick = (alignmentType: TEXT_ALIGNMENT) => {
    const isSameAlignment = values.headerContentAlignment === alignmentType;
    // if the clicked alignment is already selected and we are in bulk edit mode, deselect it
    if (isSameAlignment && isBulkEditMode) {
      setFieldValue('headerContentAlignment', null);
      return;
    }
    setFieldValue('headerContentAlignment', alignmentType);
  };

  const handleHeaderOrientationClick = (orientationType: TEXT_ORIENTATION) => {
    const isSameOrientation =
      values.headerContentOrientation === orientationType;
    // if the clicked orientation is already selected and we are in bulk edit mode, deselect it
    if (isSameOrientation && isBulkEditMode) {
      setFieldValue('headerContentOrientation', null);
      return;
    }
    setFieldValue('headerContentOrientation', orientationType);
  };

  const handleColorPickerInputChange = (
    field: keyof ILandingPageBuilderBulkEditFormData,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const hex = e.target.value;
    if (isBulkEditMode) {
      // in bulk edit mode, allow empty value
      if (hex === '') {
        setFieldValue(field, null);
        return;
      }
    }
  };

  return (
    <FormContainer title='Header'>
      <FormSection title='Typography'>
        <FormikDropdownField
          options={allFontFamilyOptions}
          name={'headerFontFamily'}
          creatable={false}
          placeholder='Font'
          label={'Select a Font'}
          isRequired={false}
          disabled={false}
          hideError
        />
        <Flex flexDirection='row' gap='8px'>
          <FormikDropdownField
            options={fontSizeOptions}
            name={'titleFontSize'}
            creatable={false}
            placeholder='Title Size'
            label='Font Size'
            isRequired={false}
            disabled={false}
            extendStyles={{
              mainWrapper: {
                minWidth: '131px',
                width: '40%',
              },
              labelStyles: {
                fontSize: '14px',
              },
            }}
            hideError
          />
          <FormikNumberField
            label='Line height'
            name='headerLineHeight'
            suffix='px'
            min={0}
            isRequired={false}
            hideError
            onBlur={() => onFormikNumberInputBlur('headerLineHeight')}
          />
        </Flex>
        <FormikDropdownField
          key={values.headerFontFamily}
          options={filteredFontWeights}
          name={'headerFontWeight'}
          creatable={false}
          placeholder='Font Weight'
          label='Font Weight'
          isRequired={false}
          disabled={false}
          extendStyles={{
            mainWrapper: {
              minWidth: '131px',
            },
            labelStyles: {
              fontSize: '14px',
            },
          }}
          hideError
        />
        <FormikDropdownField
          name='headerFontCase'
          options={fontCaseOptions}
          label='Case'
          disabled={false}
          hideError
        />
      </FormSection>
      <FormSection title='Color'>
        <Gap>
          <FormikColorPicker
            label='Header Background Color'
            name='headerBackgroundColor'
            extendStyles={commonFormikColorPickerStyle}
            onInputChange={e =>
              handleColorPickerInputChange('headerBackgroundColor', e)
            }
          />
          <FormikColorPicker
            label='Text Color'
            name='headerTextColor'
            extendStyles={commonFormikColorPickerStyle}
            onInputChange={e =>
              handleColorPickerInputChange('headerTextColor', e)
            }
          />
        </Gap>
      </FormSection>
      <FormSection title='Shadow'>
        <FormikSwitchField
          name='showHeaderShadow'
          label='Show Shadow'
          extendStyles={{
            labelStyles: {
              fontSize: '15px',
              lineHeight: '24px',
              fontWeight: 500,
              color: themes.colors.neutral[100],
            },
          }}
          onChange={() => {
            setFieldValue('showHeaderShadow', values.showHeaderShadow ? 0 : 1);
          }}
        />
      </FormSection>
      <FormSection title='Logo'>
        <FormikImageUpload
          name='headerLogo'
          extendStyles={{ containerStyles: { height: '120px' } }}
          useCropper={false}
        />
        <Flex flexDirection='row' gap='8px'>
          <FormikNumberField
            label='Max width'
            name='headerLogoMaxWidth'
            suffix='px'
            min={0}
            isRequired={false}
            hideError
            onBlur={() => onFormikNumberInputBlur('headerLogoMaxWidth')}
          />
          <FormikNumberField
            label='Max height'
            name='headerLogoMaxHeight'
            suffix='px'
            min={0}
            isRequired={false}
            hideError
            onBlur={() => onFormikNumberInputBlur('headerLogoMaxHeight')}
          />
        </Flex>
      </FormSection>
      <FormSection title='Contact info'>
        <Gap
          flexDirection='row'
          alignItems='baseline'
          justifyContent='space-between'
          width='100%'
        >
          <Flex gap='16px' flexGrow={0} width='fit-content'>
            <FormikCheckboxField
              name={'showPhone1'}
              label='Phone 1'
              onChange={() => {
                setFieldValue('showPhone1', values.showPhone1 ? 0 : 1);
              }}
              extendStyles={commonLabelStyle}
            />
            <FormikCheckboxField
              name={'showEmail'}
              label='Email'
              onChange={() => {
                setFieldValue('showEmail', values.showEmail ? 0 : 1);
              }}
              extendStyles={commonLabelStyle}
            />
            <FormikCheckboxField
              name={'showFirstName'}
              label='First Name'
              onChange={() => {
                setFieldValue('showFirstName', values.showFirstName ? 0 : 1);
              }}
              extendStyles={commonLabelStyle}
            />
            <FormikCheckboxField
              name={'showDepartment'}
              label='Department'
              onChange={() => {
                setFieldValue('showDepartment', values.showDepartment ? 0 : 1);
              }}
              extendStyles={commonLabelStyle}
            />
            <FormikCheckboxField
              name={'showWebsite'}
              label='Website'
              onChange={() => {
                setFieldValue('showWebsite', values.showWebsite ? 0 : 1);
              }}
              extendStyles={commonLabelStyle}
            />
          </Flex>
          <Flex gap='16px' flexGrow={0} width='fit-content'>
            <FormikCheckboxField
              name={'showLastName'}
              label='Last Name'
              onChange={() => {
                setFieldValue('showLastName', values.showLastName ? 0 : 1);
              }}
              extendStyles={commonLabelStyle}
            />

            <FormikCheckboxField
              name={'showPhone2'}
              label='Phone 2'
              onChange={() => {
                setFieldValue('showPhone2', values.showPhone2 ? 0 : 1);
              }}
              extendStyles={commonLabelStyle}
            />

            <FormikCheckboxField
              name={'showTitle'}
              label='Title'
              onChange={() => {
                setFieldValue('showTitle', values.showTitle ? 0 : 1);
              }}
              extendStyles={commonLabelStyle}
            />
          </Flex>
        </Gap>
      </FormSection>
      <FormSection title='Content alignment'>
        <Gap gap='20px' flexDirection='row'>
          <Button
            variant='secondary'
            icon={<FaAlignLeft />}
            extendStyles={
              values.headerContentAlignment === TEXT_ALIGNMENT.LEFT
                ? commonSelectedStyle
                : {}
            }
            onClick={() => handleHeaderAlignmentClick(TEXT_ALIGNMENT.LEFT)}
          />
          <Button
            variant='secondary'
            icon={<FaAlignCenter />}
            extendStyles={
              values.headerContentAlignment === TEXT_ALIGNMENT.CENTER
                ? commonSelectedStyle
                : {}
            }
            onClick={() => handleHeaderAlignmentClick(TEXT_ALIGNMENT.CENTER)}
          />
          <Button
            variant='secondary'
            icon={<FaAlignRight />}
            extendStyles={
              values.headerContentAlignment === TEXT_ALIGNMENT.RIGHT
                ? commonSelectedStyle
                : {}
            }
            onClick={() => handleHeaderAlignmentClick(TEXT_ALIGNMENT.RIGHT)}
          />
        </Gap>
      </FormSection>
      <FormSection title='Content orientation'>
        <Gap flexDirection='column' alignItems='flex-start' gap='16px'>
          <Flex gap='16px'>
            <FormikRadioField
              name='headerContentOrientation'
              label='Vertical'
              value={TEXT_ORIENTATION.VERTICAL}
              extendStyles={{ labelWrapper: { marginLeft: '36px' } }}
              onClick={() =>
                handleHeaderOrientationClick(TEXT_ORIENTATION.VERTICAL)
              }
            />
            <FormikRadioField
              name='headerContentOrientation'
              value={TEXT_ORIENTATION.HORIZONTAL}
              label='Horizontal'
              extendStyles={{ labelWrapper: { marginLeft: '36px' } }}
              onClick={() =>
                handleHeaderOrientationClick(TEXT_ORIENTATION.HORIZONTAL)
              }
            />
          </Flex>
        </Gap>
      </FormSection>
      <FormSection title='Icons'>
        <FormikSwitchField
          name='showContactIcons'
          label='Show Icons'
          extendStyles={{
            labelStyles: {
              fontSize: '15px',
              lineHeight: '24px',
              fontWeight: 500,
              color: themes.colors.neutral[100],
            },
          }}
          onChange={() => {
            setFieldValue('showContactIcons', values.showContactIcons ? 0 : 1);
          }}
        />
      </FormSection>
    </FormContainer>
  );
};
